import { useEffect, useState } from "react";
import Router, { useRouter } from "next/router";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import ReactGA from "react-ga";
import { useQuery } from "@apollo/client";
import Header from "./header/Header";
import Footer from "./footer/Footer";
import styles from "./layouts.module.scss";
import config from "../../common/config";
import {
  buildFavListingsObj,
  getLastFavIDsSync,
  getPathLevel,
  getStoredFavListingIDs,
  storeFavListingIDs,
} from "../../lib/helpers";
import { URL_LEVELS } from "../../common/constants";
import { useAuthState } from "../../contexts/auth/auth.provider";
import { GET_FAV_IDS } from "../../graphql/queries/get-favs";
import { GET_NEW_MSGS_COUNT } from "../../graphql/queries/user";
import { useFavListingsDispatch } from "../../contexts/fav-listings/favListings.provider";
import actions from "../../contexts/fav-listings/favListings.actions";
import { useAppDispatch } from "../../contexts/app/app.provider";
import appActions from "../../contexts/app/app.actions";
import listingsActions from "../../contexts/listingsList/listingsList.actions";

import { useListingListDispatch } from "../../contexts/listingsList/listingsList.provider";
import GenericBanner from "./genericBanner";

const { setFavListings } = actions;
const { setIsMapPage, setIsDailyRentPage, setNewMsgsCount } = appActions;

const UserSidebar = dynamic(() => import("./sidebar/UserSidebar"), {
  ssr: false,
});

const SearchBar = dynamic(() => import("./searchbar/searchbar"), {
  ssr: false,
});

// TODO: remove the dynamic import for SEO purposes for services popup
const ServicesPopup = dynamic(() => import("./popup/ServicesPopup"), {
  ssr: false,
});

const ONE_MINUTE = 60000;

const AppLayout = ({ children, type, isDailyRentPage }) => {
  const router = useRouter();
  const dispatchApp = useAppDispatch();
  const dispatch = useFavListingsDispatch();
  const token = useAuthState("token");
  const [isGALoaded, setIsGALoaded] = useState(false);
  const [skipGetFavIDs, setSkipGetFavIDs] = useState(true);
  const listingListDispatch = useListingListDispatch();
  useQuery(GET_FAV_IDS, {
    context: { token },
    fetchPolicy: "no-cache",
    skip: skipGetFavIDs,
    onCompleted: (data) => {
      const favListingIDs = data.User.getFavIDs;
      const favListingsObject = buildFavListingsObj(favListingIDs, Date.now());
      storeFavListingIDs(favListingsObject);
      dispatch(setFavListings(favListingsObject));
    },
  });

  useQuery(GET_NEW_MSGS_COUNT, {
    context: { token },
    skip: !token,
    fetchPolicy: "no-cache",
    pollInterval: ONE_MINUTE,
    onCompleted: (data) => {
      const newMsgs = (data?.Chat?.msgs?.list || [])[0]?.new_msgs;
      dispatchApp(setNewMsgsCount(newMsgs || 0));
    },
  });

  useEffect(() => {
    function handle() {
      listingListDispatch(listingsActions.toggleSearchbar(false));
    }
    window.addEventListener("popstate", handle);
    return () => {
      window.removeEventListener("popstate", handle);
    };
  }, []);

  const handleRouteChangeComplete = () => {
    const { asPath: url, query } = Router;
    const { params } = query;

    ReactGA.set({ page: url });
    ReactGA.pageview(url);

    // handle open details page
    if (params && getPathLevel(params)?.level === URL_LEVELS.LISTING) {
      ReactGA.event({
        category: "open_details_page",
        action: "opened details page",
      });
    }

    // Handle filter by price event
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has("price_range")) {
      ReactGA.event({
        category: "filter_by_price",
        action: "filter listings by price range",
      });
    }
  };

  useEffect(() => {
    dispatchApp(setIsMapPage(type === "no-footer"));
    dispatchApp(setIsDailyRentPage(isDailyRentPage));
  }, [type, isDailyRentPage, router.asPath]);

  useEffect(() => {
    const ONE_HOUR = 60 * 60 * 1000;
    const lastSyncDate = getLastFavIDsSync();
    const skip = !token || Date.now() - lastSyncDate < ONE_HOUR;
    setSkipGetFavIDs(skip);
  }, [token]);

  useEffect(() => {
    if (token) {
      const storedFavListingsObject = getStoredFavListingIDs();
      if (storedFavListingsObject) {
        dispatch(setFavListings(storedFavListingsObject));
      }
    } else {
      storeFavListingIDs({});
      dispatch(setFavListings({}));
    }
  }, [token]);

  useEffect(() => {
    if (!isGALoaded) {
      ReactGA.initialize(config.GA_TRACKING_ID);

      if (window.location.search === "") {
        // This is because of a bug in Next.js as it does not fire routeChangeComplete on first load unless there is
        // query params. We do as so to prevent double count on GA.
        handleRouteChangeComplete();
      }
      setIsGALoaded(true);
    }

    Router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, []);

  return (
    <div dir="rtl" className={styles.layoutWrapper}>
      <GenericBanner />
      <SearchBar />
      <UserSidebar />
      <ServicesPopup />
      <Header />
      {children}
      {type !== "no-footer" && <Footer />}
    </div>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  isDailyRentPage: PropTypes.bool,
};

AppLayout.defaultProps = {
  children: null,
  type: undefined,
  isDailyRentPage: false,
};

export default AppLayout;
